/**
 * ErrorBoundary — top-level React error boundary.
 *
 * Wraps the entire app so that any uncaught render/lifecycle error shows
 * a friendly fallback ("Something went wrong — please reload") instead
 * of blanking the page to white. Also POSTs the error to the backend so
 * we can diagnose per-user crashes (e.g. dual-role parents on prod who
 * report "blank page on At Home tab").
 *
 * Safety-first: the reporter never throws, never blocks the fallback UI.
 */
import { Component } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    // Best-effort telemetry — silent on failure.
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const payload = {
        message: String(error?.message || ''),
        stack: String(error?.stack || '').slice(0, 4000),
        component_stack: String(info?.componentStack || '').slice(0, 4000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_email: user?.email || null,
        user_role: user?.role || null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      };
      axios
        .post(`${BACKEND_URL}/api/client-errors`, payload, { timeout: 5000 })
        .catch(() => {});
    } catch {
      /* never let telemetry break the fallback */
    }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleReset = () => {
    try {
      localStorage.removeItem('parentapp_selected_child_id');
    } catch { /* noop */ }
    try { window.location.href = '/parent-app/home'; } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-[linear-gradient(180deg,#FFF8E1_0%,#EAD7D1_100%)]"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full rounded-3xl bg-white/90 backdrop-blur border border-white shadow-lg p-6 text-center space-y-4">
          <h1 className="font-serif text-2xl text-[#3a2f4f]">Something went wrong</h1>
          <p className="text-sm text-stone-600 leading-relaxed">
            We couldn't load this page. Our team has been notified. Please try
            reloading — if it keeps happening, message us on WhatsApp and we'll
            fix it for you right away.
          </p>
          {this.state.message && (
            <p className="text-xs text-stone-400 italic break-words">
              {this.state.message}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-full bg-[#7C6F9C] text-white text-sm font-medium hover:bg-[#6B5E8A]"
              data-testid="error-boundary-reload-btn"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 rounded-full border border-[#7C6F9C]/40 text-[#7C6F9C] text-sm font-medium hover:bg-[#7C6F9C]/5"
              data-testid="error-boundary-reset-btn"
            >
              Go to At Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
